"use client";

import Link from "next/link";
import Image from "next/image";
import { ShoppingCart, Star, Heart, Eye } from "lucide-react";
import { Product } from "@/actions/products";

interface ProductCardProps {
    product: Product;
    onQuickView?: () => void;
}

export function ProductCard({ product, onQuickView }: ProductCardProps) {
    const discount = product.price && product.salePrice
        ? Math.round(((product.price - product.salePrice) / product.price) * 100)
        : 0;

    return (
        <div className="group bg-white rounded-2xl border border-gray-100 hover:border-blue-200 hover:shadow-xl transition-all duration-300 flex flex-col overflow-hidden relative">
            {/* Badges */}
            <div className="absolute top-3 left-3 z-10 flex flex-col gap-2">
                {product.price && product.salePrice && (
                    <span className="bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-md">
                        -{discount}%
                    </span>
                )}
                {product.isNew && (
                    <span className="bg-blue-600 text-white text-xs font-bold px-2 py-1 rounded-md">
                        NEW
                    </span>
                )}
            </div>

            {/* Image Area */}
            <div className="relative w-full aspect-square bg-gray-50 overflow-hidden">
                <Image
                    src={product.images[0]}
                    alt={product.name}
                    fill
                    className="object-cover object-center p-4 group-hover:scale-105 transition-transform duration-500"
                />

                {/* Quick Actions Overlay */}
                <div className="absolute inset-0 bg-black/5 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                    <button className="w-10 h-10 bg-white rounded-full flex items-center justify-center text-gray-600 hover:text-blue-600 hover:scale-110 transition-all shadow-lg" title="Add to Wishlist">
                        <Heart className="w-5 h-5" />
                    </button>
                    <button
                        className="w-10 h-10 bg-white rounded-full flex items-center justify-center text-gray-600 hover:text-blue-600 hover:scale-110 transition-all shadow-lg"
                        title="Quick View"
                        onClick={(e) => {
                            e.preventDefault(); // Prevent Link navigation
                            onQuickView?.();
                        }}
                    >
                        <Eye className="w-5 h-5" />
                    </button>
                </div>
            </div>

            {/* Content Area */}
            <div className="p-4 flex flex-col flex-grow">
                <div className="mb-2">
                    <span className="text-xs text-gray-400 font-medium uppercase tracking-wider">{product.brand}</span>
                    <h3 className="font-bold text-gray-900 leading-tight group-hover:text-blue-600 transition-colors line-clamp-2 min-h-[2.5rem]">
                        <Link href={`/products/${product.slug}`}>
                            {product.name}
                        </Link>
                    </h3>
                </div>

                {/* Rating */}
                <div className="flex items-center gap-1 mb-3">
                    <div className="flex text-yellow-400">
                        {[...Array(5)].map((_, i) => (
                            <Star
                                key={i}
                                className={`w-3 h-3 ${i < Math.floor(product.rating) ? "fill-current" : "text-gray-200"}`}
                            />
                        ))}
                    </div>
                    <span className="text-xs text-gray-400">({product.reviews})</span>
                </div>

                {/* Price & Action */}
                <div className="mt-auto flex items-center justify-between pt-4 border-t border-gray-50">
                    <div className="flex flex-col">
                        {product.price ? (
                            product.salePrice ? (
                                <>
                                    <span className="text-xs text-gray-400 line-through">LKR {product.price.toLocaleString()}</span>
                                    <span className="text-lg font-bold text-blue-600">LKR {product.salePrice.toLocaleString()}</span>
                                </>
                            ) : (
                                <span className="text-lg font-bold text-gray-900">LKR {product.price.toLocaleString()}</span>
                            )
                        ) : (
                            <span className="text-lg font-bold text-blue-600">Call for Price</span>
                        )}
                    </div>

                    <a
                        href={`https://wa.me/94707336060?text=${encodeURIComponent(`Hi CNS, I'm interested in: ${product.name} ${product.price ? `(LKR ${product.salePrice || product.price})` : ''}. Is it available?`)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-10 h-10 rounded-xl bg-green-50 text-green-600 flex items-center justify-center hover:bg-green-600 hover:text-white transition-colors"
                        title="Order via WhatsApp"
                    >
                        <ShoppingCart className="w-5 h-5" />
                    </a>
                </div>
            </div>
        </div>
    );
}
